// Loads and holds the place data. Free spots ship with full detail; locked
// spots are anonymized stubs (id/lat/lng/type) until unlock replaces them with
// full data from the Worker (Phase 3).

let _places = []

export async function loadPlaces() {
  const [free, locked] = await Promise.all([
    fetch('/data/places.free.json').then((r) => r.json()),
    fetch('/data/places.locked.json').then((r) => r.json()).catch(() => []),
  ])
  _places = [...free.map(normalize), ...locked.map(normalize)]
  return _places
}

function normalize(p) {
  return {
    id: p.id,
    lat: p.lat,
    lng: p.lng,
    type: p.type || 'restaurant',
    locked: !!p.locked,
    free: !!p.free, // true only for the bundled teaser spots
    name: p.name || null,
    cuisine: p.cuisine || '',
    arrondissement: p.arrondissement ?? null,
    neighbourhood: p.neighbourhood || '',
    price: p.price ?? null,
    blurb: p.blurb || '',
    article_url: p.article_url || '',
    image: p.image || '',
    address: p.address || '',
    tags: p.tags || [],
    photos: p.photos || [],
    michelin: p.michelin ?? null,
    teaser: p.teaser || '',
    blur: p.blur || '',
  }
}

export function getPlaces() {
  return _places
}

// ---- quartier (neighbourhood) ----
// Use the curated neighbourhood when present, else fall back to the
// arrondissement so the quartier filter works on the auto-extracted data.
export function quartierOf(p) {
  if (p.neighbourhood) return p.neighbourhood
  if (p.arrondissement) return `${p.arrondissement}e arr.`
  return ''
}

export function quartiers() {
  const counts = new Map()
  for (const p of _places) {
    const q = quartierOf(p)
    if (q) counts.set(q, (counts.get(q) || 0) + 1)
  }
  // most-populated first, but arrondissements in numeric order when no hoods
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([q]) => q)
}

// ---- search ----
// Accent-fold so "peniche" matches "Péniche", "creme" matches "crème" etc.
// — handy for English speakers typing without French accents.
const fold = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export function matchesSearch(p, q) {
  if (!q) return true
  if (p.locked) return false // stubs carry no searchable text
  const hay = fold([p.name, p.cuisine, p.neighbourhood, quartierOf(p), p.blurb, p.type,
    ...(p.tags || [])]
    .filter(Boolean).join(' '))
  return hay.includes(fold(q))
}

// ---- saved shortlist (localStorage) ----
const SAVED_LS = 'pe_saved'
let _saved = new Set(loadSaved())

function loadSaved() {
  try { return JSON.parse(localStorage.getItem(SAVED_LS) || '[]') } catch { return [] }
}
export function isSaved(id) { return _saved.has(id) }
export function savedCount() { return _saved.size }
export function toggleSaved(id) {
  _saved.has(id) ? _saved.delete(id) : _saved.add(id)
  localStorage.setItem(SAVED_LS, JSON.stringify([..._saved]))
  return _saved.has(id)
}

// Replace locked stubs with full paid data after unlock. Re-lock anything not
// in `paid` (e.g. licence revoked → paid is null/empty).
export function applyPaid(paid) {
  if (!paid) {
    _places = _places.map((p) => (p.free ? p : { ...p, locked: true }))
    return
  }
  const full = new Map(paid.map((p) => [p.id, p]))
  _places = _places.map((p) => {
    if (p.free) return p
    const f = full.get(p.id)
    return f ? normalize({ ...f, locked: false }) : { ...p, locked: true }
  })
}

export function lockedCount() {
  return _places.filter((p) => p.locked).length
}

// Locked spots within radiusKm of a {lat,lng} — powers the location-aware paywall.
export function nearbyLocked(pos, radiusKm = 1) {
  if (!pos) return []
  return _places.filter((p) => p.locked && p.lat != null
    && haversineKm(pos, p) <= radiusKm)
}

function haversineKm(a, b) {
  const R = 6371
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

// Distinct neighbourhoods present in unlocked data, for the filter dropdown.
export function neighbourhoods() {
  return [...new Set(_places.filter((p) => p.neighbourhood).map((p) => p.neighbourhood))].sort()
}

export function allTags() {
  const c = new Map()
  for (const p of _places) for (const t of p.tags) c.set(t, (c.get(t) || 0) + 1)
  return [...c.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t)
}

// Build a GeoJSON FeatureCollection for MapLibre from a filtered list.
export function toGeoJSON(places) {
  return {
    type: 'FeatureCollection',
    features: places.map((p) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      properties: { id: p.id, type: p.type, locked: p.locked ? 1 : 0 },
    })),
  }
}

export function byId(id) {
  return _places.find((p) => p.id === id)
}
